import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Switch, Typography } from '@mui/material';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { CustomContentProps, SnackbarContent, useSnackbar } from 'notistack';
import { forwardRef, useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { useCancelTask } from '~/backend/connector-api/connector-components';
import { checkIsInSessionRouteWithoutRouter } from '~/hooks/session/useIsInSessionRoute';
import { useJobManagement } from '~/hooks/workflows/useJobManagement';
import {
    isJobRunning,
    Job,
    jobQueueExpandedState,
} from '~/state/atoms/jobAtoms';
import { autoLoadResultsState } from '~/state/atoms/viewerAtoms';
import JobItem from './JobItem';

interface JobQueueProps extends CustomContentProps {
    allowDownload?: boolean;
}

const JobQueue = forwardRef<HTMLDivElement, JobQueueProps>(({ id }, ref) => {
    const [autoLoad, setAutoLoad] = useRecoilState(autoLoadResultsState);
    const { jobQueue, setJobQueue, setJobStatus } = useJobManagement();
    const { closeSnackbar } = useSnackbar();
    const [expanded, setExpanded] = useRecoilState(jobQueueExpandedState);
    const { mutateAsync: cancelConnectorTask } = useCancelTask();

    const hasRunningJobs = jobQueue.jobs.some((job) =>
        isJobRunning(job.status)
    );

    const handleExpandClick = useCallback(() => {
        setExpanded((oldExpanded) => !oldExpanded);
    }, []);

    const handleDismiss = useCallback(() => {
        closeSnackbar(id);
        setJobQueue((prev) => ({
            ...prev,
            snackbarId: undefined,
        }));
    }, [id, closeSnackbar]);

    const clearJob = useCallback(
        (job: Job) => {
            let shouldDismiss = false;
            setJobQueue((prev) => {
                const updatedJobs = prev.jobs.filter((j) => j.id !== job.id);
                // Check if the task list will be empty after this update
                if (updatedJobs.length === 0) {
                    shouldDismiss = true;
                }
                return {
                    ...prev,
                    jobs: updatedJobs,
                };
            });
            if (shouldDismiss) {
                handleDismiss();
            }
        },
        [setJobQueue, handleDismiss]
    );

    const cancelJob = useCallback(
        async (job: Job) => {
            setJobStatus(job.id, 'Canceled', 'Cancelled');
            if (checkIsInSessionRouteWithoutRouter()) {
                await cancelConnectorTask({
                    pathParams: {
                        taskID: job.id,
                    },
                });
            } else if (job.client) {
                job.client.abort();
            }
        },
        [cancelConnectorTask, clearJob]
    );

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setAutoLoad(event.target.checked);
        },
        [setAutoLoad]
    );

    return (
        <SnackbarContent ref={ref}>
            <Card
                sx={(theme) => ({
                    width: '300px',
                    background: 'rgba(4, 30, 52, 0.65)',
                    borderRadius: `${theme.shape.borderRadius}px`,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.27)',
                    backdropFilter: 'blur(5px)',
                    WebkitBackdropFilter: 'blur(5px)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    p: 1,
                })}
            >
                <CardActions
                    sx={{
                        justifyContent: 'space-between',
                        display: 'flex',
                        cursor: 'pointer',
                        padding: 0,
                        px: 1,
                    }}
                    onClick={handleExpandClick}
                >
                    <Box display="flex">
                        <Typography fontWeight={500} mt={0.5} fontSize={14}>
                            Task List
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                        {hasRunningJobs && !expanded && (
                            <CircularProgress size={16} thickness={5} />
                        )}

                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            ml={1}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Typography fontSize={11} position="relative">
                                Auto-load
                            </Typography>
                            <Switch
                                checked={autoLoad}
                                onChange={handleChange}
                                inputProps={{ 'aria-label': 'controlled' }}
                                size="small"
                                data-testid="auto-load-results-switch"
                            />
                        </Box>

                        <IconButton
                            data-testid="toggleJobQueueButton"
                            aria-label="Show more"
                            size="small"
                            sx={{
                                transform: expanded
                                    ? 'rotate(0deg)'
                                    : 'rotate(180deg)',
                                transition: 'all .2s',
                            }}
                        >
                            <ExpandMoreIcon />
                        </IconButton>
                    </Box>
                </CardActions>
                <Collapse in={expanded} timeout="auto">
                    <Box display="flex" flexDirection="column" gap={1} mt={1}>
                        {jobQueue.jobs.map((job) => (
                            <JobItem
                                key={job.id}
                                job={job}
                                onClear={clearJob}
                                onCancel={cancelJob}
                            />
                        ))}
                    </Box>
                </Collapse>
            </Card>
        </SnackbarContent>
    );
});

JobQueue.displayName = 'JobQueue';
export default JobQueue;
